import { useState, useEffect, useMemo, useCallback } from 'react'
import { Button, Typography, Divider, Spin, Empty } from 'antd'
import { DeleteOutlined, UserAddOutlined } from '@ant-design/icons'

import AvatarWithText from '@/components/UI/Avatar/AvatarWithText'
import { CustomModal } from '@/components'
import { CustomDataTable } from '@/components/UI/Tables/Datatable'

const { Text } = Typography

const AssignedUsersModal = ({
  modalOpen,
  handleClose,
  role,
  setShowNewUsersModal,
  onSave,
  onFetchAssignedUsers,
  onAddNewUsersToList,
  onAssignedUsersChanged,
  allUsers = [],
  rightOffset = 0,
}) => {
  const [selectedUsers, setSelectedUsers] = useState([])
  const [assignedUsers, setAssignedUsers] = useState([])
  const [initialAssignedUsers, setInitialAssignedUsers] = useState([])
  const [hasChanges, setHasChanges] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isError, setIsError] = useState(false)

  // Fetch assigned users when modal opens
  useEffect(() => {
    if (modalOpen && role?.id) {
      fetchAssignedUsers()
    }
  }, [modalOpen, role?.id])
  
  // Add callback to handle new users being added
  const handleNewUsersAdded = useCallback((newUserIds) => {
    // Find the full user objects from allUsers
    const newUsers = allUsers.filter(user => newUserIds.includes(user.id));
    
    // Add them to assignedUsers (avoid duplicates)
    setAssignedUsers(prev => {
      const existingIds = new Set(prev.map(u => u.id || u.employeeId));
      const usersToAdd = newUsers.filter(u => !existingIds.has(u.id));
      return [...prev, ...usersToAdd];
    });
  }, [allUsers])

  // Check for changes and notify parent
  useEffect(() => {
    const currentIds = assignedUsers.map((user) => user.id || user.employeeId).sort()
    const initialIds = initialAssignedUsers.map((user) => user.id || user.employeeId).sort()
    const hasChangesNow =
      currentIds.length !== initialIds.length ||
      !currentIds.every((id, index) => id === initialIds[index])
    setHasChanges(hasChangesNow)
  }, [assignedUsers, initialAssignedUsers])
  
  // Notify parent of current assigned user IDs separately to avoid infinite loop
  useEffect(() => {
    if (onAssignedUsersChanged && assignedUsers.length >= 0) {
      const currentIds = assignedUsers.map((user) => user.id || user.employeeId);
      onAssignedUsersChanged(currentIds);
    }
  }, [assignedUsers]) // Remove onAssignedUsersChanged from dependencies

  const fetchAssignedUsers = async () => {
    setIsLoading(true)
    setIsError(false)
    try {
      const roleId = role.id || role.roleId;
      const users = await onFetchAssignedUsers(roleId)
      setAssignedUsers(users)
      setInitialAssignedUsers(users)
    } catch (error) {
      setIsError(true)
    } finally {
      setIsLoading(false)
    }
  }

  // Transform assigned users for table display
  const users = useMemo(() => {
    return assignedUsers.map((user) => ({
      key: user.id?.toString() || user.employeeId?.toString(),
      employeeId: user.id || user.employeeId,
      name: [user.first_name || user.firstName, user.middle_name || user.middleName, user.last_name || user.lastName]
        .filter(Boolean)
        .join(' '),
      email: user.email || 'N/A',
      team: user.teams.map((team) => team.name).join(', ') || 'N/A',
    }))
  }, [assignedUsers])

  // Define Ant Design table columns
  const userColumns = [
    {
      title: 'NAME',
      dataIndex: 'name',
      key: 'name',
      width: '30%',
    },
    {
      title: 'EMAIL',
      dataIndex: 'email',
      key: 'email',
      width: '35%',
    },
    {
      title: 'TEAM',
      dataIndex: 'team',
      key: 'team',
      width: '35%',
    },
  ]

  const handleSelectionChange = useCallback((selectedRowKeys) => {
    setSelectedUsers(selectedRowKeys)
  }, [])

  // Handle bulk removal via checkboxes
  const handleBulkRemove = () => {
    if (selectedUsers.length === 0) return;
    const idsToRemove = new Set(selectedUsers.map(id => parseInt(id)));
    const updatedAssignedUsers = assignedUsers.filter(
      user => !idsToRemove.has(user.id || user.employeeId)
    );
    setAssignedUsers(updatedAssignedUsers);
    setSelectedUsers([]);
  }

  const handleSave = async () => {
    const assignedUserIds = assignedUsers?.map((user) => user.id || user.employeeId)
    const roleId = role?.id || role?.roleId;
    
    if (roleId) {
      const payload = {
        roleId: roleId,
        employeeIds: assignedUserIds,
      }
      setIsSaving(true)
      try {
        await onSave(payload)
        setInitialAssignedUsers([...assignedUsers])
        setHasChanges(false)
        handleClose()
      } catch (error) {
      } finally {
        setIsSaving(false)
      }
    }
  }

  const handleCancel = () => {
    if (hasChanges) {
      setAssignedUsers(initialAssignedUsers)
      setHasChanges(false)
    }
    setSelectedUsers([])
    handleClose()
  }

  const handleRetry = () => {
    fetchAssignedUsers()
  }

  const roleName = role?.name || role?.role_name || 'Unknown Role'

  // Expose the callback for parent component
  useEffect(() => {
    if (onAddNewUsersToList) {
      onAddNewUsersToList(handleNewUsersAdded);
    }
  }, [onAddNewUsersToList, handleNewUsersAdded]);

  // Row selection configuration
  const rowSelection = {
    selectedRowKeys: selectedUsers,
    onChange: handleSelectionChange,
  }

  // Action buttons for table header - shown when users are selected
  const actionButtons = selectedUsers.length > 0
    ? [
        {
          label: `Remove ${selectedUsers.length} user${selectedUsers.length > 1 ? 's' : ''}`,
          icon: <DeleteOutlined />,
          onClick: handleBulkRemove,
          type: 'primary',
          danger: true,
        },
      ]
    : []

  // Row actions for individual user removal
  const rowActions = [
    {
      label: 'Remove',
      icon: <DeleteOutlined />,
      onClick: (record) => {
        const updatedAssignedUsers = assignedUsers.filter(
          (user) => (user.id || user.employeeId) !== parseInt(record.key)
        )
        setAssignedUsers(updatedAssignedUsers)
      }
    }
  ]

  // Render error state
  const renderErrorState = () => (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      padding: '40px 0',
      gap: '16px'
    }}>
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        style={{marginBlock:'0px'}}
        description={
          <div style={{ textAlign: 'center' }}>
            <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
              Failed to Load Users
            </Text>
            <br />
            <Text style={{ fontSize: '12px', color: '#8c8c8c' }}>
              Unable to fetch assigned users. Please try again.
            </Text>
          </div>
        }
      />
      <Button
        onClick={handleRetry}
        style={{ marginTop: '8px', paddingLeft: '20px', paddingRight: '20px' }}
      >
        Try Again
      </Button>
    </div>
  )

  // Render empty state
  const renderEmptyState = () => (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      padding: '40px 0',
      gap: '16px'
    }}>
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <div style={{ textAlign: 'center' }}>
            <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>
              Assign User
            </Text>
            <br />
            <Text style={{ fontSize: '12px', color: '#8c8c8c' }}>
              No user has been assigned to this role
            </Text>
          </div>
        }
      />
      <Button
        type="primary"
        onClick={() => {
          setShowNewUsersModal && setShowNewUsersModal(true);
        }}
        style={{ margin: '8px 0px 20px 0px', paddingLeft: '20px', paddingRight: '20px', }}
      >
        Assign a user
      </Button>
    </div>
  )

  return (
    <CustomModal
      isOpen={modalOpen}
      onClose={handleCancel}
      title="Assign users"
      subtitle="Select users to assign to this role"
      primaryActionText="Save"
      secondaryActionText="Cancel"
      onPrimaryAction={handleSave}
      onSecondaryAction={handleCancel}
      width="700px"
      maxWidth="md"
      primaryActionDisabled={!hasChanges || isLoading}
      loading={isSaving}
      rightOffset={rightOffset}
    >
      <div style={{ marginBottom: '24px', marginTop: '16px' }}>
        <AvatarWithText text={roleName} />
      </div>

      <div style={{ 
        marginTop: '16px', 
        border: '1px solid #e5e7eb', 
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: users.length > 0 && !isLoading && !isError ? '1px solid #e5e7eb' : 'none',
        }}>
          <Text strong style={{ fontSize: '15px', lineHeight: '23px' }}>
            Assigned Users
          </Text>
          {users.length !== 0 && !isLoading && !isError && (
            <Button
              icon={<UserAddOutlined />}
              onClick={() => {
                setShowNewUsersModal && setShowNewUsersModal(true);
              }}
              style={{ paddingLeft: '10px', paddingRight: '10px' }}
            >
              Add
            </Button>
          )}
        </div>

        {isLoading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
          }}>
            <Spin size="large" />
          </div>
        ) : isError ? (
          renderErrorState()
        ) : users.length === 0 ? (
          renderEmptyState()
        ) : (
          <>
            {/* Action bar for bulk remove - shows when users are selected */}
            {selectedUsers.length > 0 && (
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#fff1f0',
                borderBottom: '1px solid #ffccc7',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <Text style={{ color: '#cf1322', fontSize: '14px' }}>
                  {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''} selected
                </Text>
                <Button
                  danger
                  type="primary"
                  icon={<DeleteOutlined />}
                  onClick={handleBulkRemove}
                  size="small"
                >
                  Remove
                </Button>
              </div>
            )}
            
            {/* Table without extra header spacing */}
            <div style={{ margin: 0 }}>
              <CustomDataTable
                data={users}
                columns={userColumns}
                loading={isLoading}
                rowSelection={rowSelection}
                selectedRowKeys={selectedUsers}
                pagination={false}
                showTitle={false}
                showSearch={false}
                showFilter={false}
                showCSV={false}
                rowActions={rowActions}
                tableClassName="border-0 rounded-none"
                headerClassName="hidden"
              />
            </div>
          </>
        )}
      </div>
    </CustomModal>
  )
}

export default AssignedUsersModal